<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Time of Day</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button type="default" @click="cancelOperation"> Cancel</el-button>
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits 
			service="Time of Day" :record="selectedTimeOfDay" />
      <div class="inline-form-group">
        <el-col :span="11">
          <el-input v-model="editForm.name" placeholder="Name">
            <template slot="prefix">Name</template>
          </el-input>
        </el-col>
        <el-col :span="1" />
        <el-col :span="12">
          <dads-select
            v-model="editForm.timezone"
            placeholder="Select a time zone"
            allow-edit
            filterable
          >
            <template slot="prefix">Time zone</template>
            <el-option label="Use tenant default" value="" />
            <el-option
              v-for="(timezone, index) in timezones"
              :key="index"
              :label="timezone.name"
              :value="timezone.name"
            />
          </dads-select>
        </el-col>
      </div>
      <el-select
        v-model="editForm.type"
        @change="hasUserChangedCustomType = true"
        placeholder="Select type"
      >
        <el-option
          v-for="(item, i) in customTypeOptions"
          :key="i"
          :label="item.label"
          :value="item.value"
        />
        <template slot="prefix">Type</template>
      </el-select>

      <!-- ⭐ WEEKTIME ⭐ -->
      <div class="week-time" v-if="customType === 'WEEKTIME'">
        <h2 class="black-text">Enable days of week</h2>
        <div
          class="inline-form-group"
          v-for="(day, index) in days"
          :key="index"
        >
          <el-col :span="1" style="align-self: start; margin-top: 20px;">
            <el-checkbox
              @change="editForm.customWeekTime[`day${index}`].enabled = $event"
              :value="editForm.customWeekTime[`day${index}`].enabled"
            />
          </el-col>
          <el-col :span="2" style="align-self: start; margin-top: 25px">
            <h4 class="black-text mt-0">{{ day }}</h4>
          </el-col>
          <el-col :span="1"></el-col>
          <el-col :span="21">
            <multi-time-range-selector
              :values="editForm.customWeekTime[`day${index}`].values"
              :disabled="!editForm.customWeekTime[`day${index}`].enabled"
              @timeRangeSelected="onTimeRangeSelection(index, $event)"
            />
          </el-col>
        </div>
      </div>

      <!-- ⭐ CALENDAR ⭐ -->
      <div v-if="customType === 'CALENDAR'">
        <h2 class="form-section-header">Select date ranges</h2>
        <div class="mt-10">
          <div
            class="date-picker inline-form-group"
            v-for="(item, i) in dateRanges"
            :key="i"
          >
            <el-col :span="21">
              <el-date-picker
                :clearable="true"
                v-model="dateRanges[i]"
                type="daterange"
                format="yyyy-MM-dd"
                align="right"
                unlink-panels
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                @input="onDateRangeSelection($event, i)"
              />
            </el-col>
            <el-col :span="1" />
            <el-col :span="2">
              <el-tooltip
                class="item"
                effect="dark"
                :content="`Remove date range ${i + 1}`"
                placement="top"
              >
                <el-button @click="removeDateRange(i)"
                           class="table-action-button icon-red"
                           icon="el-icon-delete" />
              </el-tooltip>
            </el-col>
          </div>
        </div>
        <div class="inline-form-group">
          <el-button style="float: right;" @click="addDateRange"
            >Add date range</el-button
          >
        </div>
      </div>

      <!-- ⭐ ALL or ANY ⭐ -->
      <div v-if="['ALL', 'ANY'].includes(customType)">
        <h2 class="form-section-header">Select conditions</h2>
        <dads-select
          v-model="editForm.conditionParams"
          placeholder="Select global conditions"
          multiple
          filterable
        >
          <template slot="prefix"
            ><span style="opacity: 0">Conditions</span></template
          >
          <el-option
            v-for="(item, i) in conditionOptions"
            :key="i"
            :label="item.name"
            :value="item.id"
          />
        </dads-select>
      </div>
    </el-form>
  </el-dialog>
</template>

<script>
import OmniaTimeSelect from "@/components/OmniaTimeSelect.vue";
import DadsSelect from "@/components/select";
import moment from "moment";
import RecentAudits from "@/components/RecentAudits";
import MultiTimeRangeSelector from "../../time-of-days/components/multiTimeRangeSelector.vue";

export default {
  name: "GlobalTimeOfDaysEditForm",

  props: {
    editVisible: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    timezones: { required: true },
    customTypeOptions: { required: true },
    globalConditionOptions: { required: true },
    conditionOptions: { required: true },
    selectedTimeOfDay: { required: true }
  },

  components: {
    OmniaTimeSelect,
    DadsSelect,
    RecentAudits,
    MultiTimeRangeSelector
  },

  data() {
    return {
      hasUserChangedCustomType: false,

      days: ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"],

      editForm: {
        tenantId: 0,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        conditionParams: [],
        customWeekTime: {
          day0: {
            enabled: false,
            values: []
          },
          day1: {
            enabled: false,
            values: []
          },
          day2: {
            enabled: false,
            values: []
          },
          day3: {
            enabled: false,
            values: []
          },
          day4: {
            enabled: false,
            values: []
          },
          day5: {
            enabled: false,
            values: []
          },
          day6: {
            enabled: false,
            values: []
          }
        },
        dids: [],
        dateRanges: []
      },

      dateRanges: []
    };
  },

  computed: {
    customType() {
      return this.editForm.type;
    }
  },

  watch: {
    customType(val) {
      if (this.hasUserChangedCustomType) {
        if (val !== "USEGLOBAL") {
          this.editForm.param1 = "";
        }

        if (val !== "CALENDAR") {
          this.dateRanges = [[moment(), moment()]];
          this.editForm.dateRanges = [];
        }

        if (val !== "ALL" || val !== "ANY") {
          this.editForm.conditionParams = [];
        }

        if (val !== "CUSTOMWEEKTIME") {
          this.editForm.customWeekTime = {
            day0: {
              enabled: false,
              values: []
            },
            day1: {
              enabled: false,
              values: []
            },
            day2: {
              enabled: false,
              values: []
            },
            day3: {
              enabled: false,
              values: []
            },
            day4: {
              enabled: false,
              values: []
            },
            day5: {
              enabled: false,
              values: []
            },
            day6: {
              enabled: false,
              values: []
            }
          };
        }
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    selectedTimeOfDay(editTimeOfDay) {
      if (editTimeOfDay) {
        const {
          name,
          timezone,
          type,
          param1,
          customWeekTime,
          dateRanges
        } = editTimeOfDay;
        this.editForm.name = name;
        this.editForm.timezone = timezone;
        this.editForm.type = type;
        this.editForm.param1 = param1 !== "" ? Number(param1) : "";

        if (type === "WEEKTIME" && customWeekTime) {
          Object.keys(customWeekTime).forEach(day => {
            this.editForm.customWeekTime[day] = { ...customWeekTime[day] };
          });
        }

        if (type === "ALL" || type === "ANY") {
          this.editForm.conditionParams = [...editTimeOfDay.conditionParams];
        }

        if (type === "CALENDAR") {
          if (dateRanges && dateRanges.length > 0) {
            dateRanges.forEach(({ startDate, endDate }) => {
              this.dateRanges.push([
                moment(new Date(startDate)).format("YYYY-MM-DD HH:mm:ss"),
                moment(new Date(endDate)).format("YYYY-MM-DD HH:mm:ss")
              ]);
            });
            this.editForm.dateRanges = this.dateRanges.map(
              ([startDate, endDate]) => {
                return {
                  startDate,
                  endDate
                };
              }
            );
          } else {
            this.dateRanges.push([moment(), moment()]);
          }
        } else {
          this.dateRanges.push([moment(), moment()]);
        }
      }
    }
  },

  methods: {
    onTimeRangeSelection(index, ranges) {
      this.editForm.customWeekTime[`day${index}`].values = [...ranges];
    },

    onDateRangeSelection($event, idx) {
      const startDate = moment($event[0]).format("YYYY-MM-DD HH:mm:ss");
      const endDate = moment($event[1]).format("YYYY-MM-DD HH:mm:ss");
      this.editForm.dateRanges[idx] = {
        startDate,
        endDate
      };
    },

    addDateRange() {
      this.dateRanges.push([moment(), moment()]);
    },

    removeDateRange(idx) {
      this.dateRanges.splice(idx, 1);
      this.editForm.dateRanges.splice(idx, 1);
      if (this.dateRanges.length === 0) {
        this.dateRanges.push([moment(), moment()]);
      }
    },

    editOperation() {
      const editBody = { ...this.editForm };

      this.$emit("editOperation", editBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    clearFields() {
      this.dateRanges = [];
      this.hasUserChangedCustomType = false;

      this.editForm = {
        tenantId: 0,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        conditionParams: [],
        customWeekTime: {
          day0: {
            enabled: false,
            values: []
          },
          day1: {
            enabled: false,
            values: []
          },
          day2: {
            enabled: false,
            values: []
          },
          day3: {
            enabled: false,
            values: []
          },
          day4: {
            enabled: false,
            values: []
          },
          day5: {
            enabled: false,
            values: []
          },
          day6: {
            enabled: false,
            values: []
          }
        },
        dids: [],
        dateRanges: []
      };
    }
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.week-time {
  max-width: 600px;
  margin: 0 auto;
}

.el-date-editor {
  width: 100%;
}

.date-picker {
  margin-bottom: 1.5rem;
}

.inline-form-group .el-checkbox {
  margin-left: 0;
}
</style>
